import { Dispatch, Fragment, SetStateAction } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import TrashIcon from "@heroicons/react/24/solid/TrashIcon";
import PencilIcon from "@heroicons/react/24/solid/PencilIcon";
import { MdFormatListBulleted } from "react-icons/md";
import { Control, Controller } from "react-hook-form";
import { Combobox, Transition } from "@headlessui/react";

// Local
import { ExistingPriceBookItem } from "../../models/price-book-item";
import { ExistingSiteKey } from "../../models/site-key";
import AddNewEstimateItemSelection from "../../components/estimates/AddNewEstimateItemSelection";
import { TemporaryEstimateItem } from "../../models/estimate-item";
import currencyFormatter from "../../currency";
import * as strings from "../../strings";
import { getReadableCraftType } from "../../models/craft-types";
import { getReadableTaskType } from "../../models/task-types";
import { getReadableTaskStatus } from "../../models/task-status";
import { DialogStates, TemporaryTaskGeneration } from "./types";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import StyledSwitchGroup from "../../components/StyledSwitchGroup";
import { TemplateFormState } from "./types";
import { StyledTooltip } from "../../components/StyledTooltip";
import {
  ExistingMembershipTemplate,
  getReadableInvoiceMethod,
} from "../../models/membership-template";
import { getReadableDefaultDuration } from "../../utils/templateAndMembershipUtils";

interface Props {
  siteKeyDoc: ExistingSiteKey | null;
  onFilterTextBoxChanged: (searchTerm: string) => Promise<void>;

  dialogStates: DialogStates;
  setDialogStates: Dispatch<SetStateAction<DialogStates>>;

  /** if they're editing a template, omit that template */
  templates: ExistingMembershipTemplate[];
  onFilterTemplates: (searchTerm: string) => void;

  typesensePBIs: ExistingPriceBookItem[];
  pbi: ExistingPriceBookItem | null;
  renewalPBI: ExistingPriceBookItem | null;
  renewalTemplate: ExistingMembershipTemplate | null;
  setPBI: Dispatch<SetStateAction<ExistingPriceBookItem | null>>;
  setRenewalPBI: Dispatch<SetStateAction<ExistingPriceBookItem | null>>;
  setRenewalTemplate: Dispatch<
    SetStateAction<ExistingMembershipTemplate | null>
  >;

  taskGenerationList: TemporaryTaskGeneration[];
  setTaskGenerationList: Dispatch<SetStateAction<TemporaryTaskGeneration[]>>;
  setTaskGenerationDoc: Dispatch<
    SetStateAction<TemporaryTaskGeneration | null>
  >;
}

/** Shared utility functions/components, for membership template forms */
export function getTemplateFormSections({
  templates,
  pbi,
  renewalPBI,
  renewalTemplate,
  dialogStates,
  setDialogStates,
  onFilterTextBoxChanged,
  onFilterTemplates,
  typesensePBIs,
  siteKeyDoc,
  setPBI,
  setRenewalPBI,
  setRenewalTemplate,
  taskGenerationList,
  setTaskGenerationList,
  setTaskGenerationDoc,
}: Props): {
  pbiComponent: JSX.Element;
  renewalPBIComponent: JSX.Element;
  renewalTemplateComponent: JSX.Element;
  taskGenComponent: (control: Control<TemplateFormState>) => JSX.Element;
} {
  return {
    pbiComponent: getPBIComponent({
      pbi,
      isSectionOpen: dialogStates.pbi,
      setDialogStates,
      onFilterTextBoxChanged,
      typesensePBIs,
      onSelectItem: (tempItem: TemporaryEstimateItem) => {
        const pbi = typesensePBIs.find(
          (item) => item.id === tempItem.priceBookItemID,
        );
        setPBI(pbi ?? null);
        setDialogStates((prev) => ({ ...prev, pbi: false }));
      },
      siteKeyDoc,
      setPricebookItem: setPBI,
    }),

    renewalPBIComponent: getRenewalPBIComponent({
      renewalPBI,
      isSectionOpen: dialogStates.renewalPBI,
      setDialogStates,
      onFilterTextBoxChanged,
      typesensePBIs,
      onSelectItem: (tempItem: TemporaryEstimateItem) => {
        const pbi = typesensePBIs.find(
          (item) => item.id === tempItem.priceBookItemID,
        );
        setRenewalPBI(pbi ?? null);
        setDialogStates((prev) => ({ ...prev, renewalPBI: false }));
      },
      siteKeyDoc,
      setRenewalPBI,
      isRenewalTemplateSelected: Boolean(renewalTemplate),
    }),

    renewalTemplateComponent: getRenewalTemplateComponent({
      templateList: templates,
      onFilterTemplates: onFilterTemplates,
      renewalTemplate,
      siteCurrency: siteKeyDoc?.customizations.accounting?.currency ?? "USD",
      isSectionOpen: dialogStates.renewalTemplate,
      setDialogStates,
      setRenewalTemplate,
      isRenewalPBISelected: Boolean(renewalPBI),
    }),

    taskGenComponent: (control: Control<TemplateFormState>) =>
      getTaskGenComponent({
        setDialogStates,
        taskGenerationList,
        deleteTask: (id: string) => {
          setTaskGenerationList((prev) =>
            prev.filter((task) => task.id !== id),
          );
        },
        editTask: (id: string) => {
          const taskToEdit = taskGenerationList.find((task) => task.id === id);
          if (!taskToEdit) return;
          setTaskGenerationDoc(taskToEdit);
          setDialogStates((prev) => ({ ...prev, taskGen: true }));
        },
        control,
      }),
  };
}

function getPBIComponent(args: {
  siteKeyDoc: ExistingSiteKey | null;
  onFilterTextBoxChanged: (searchTerm: string) => Promise<void>;

  isSectionOpen: boolean;
  setDialogStates: Dispatch<SetStateAction<DialogStates>>;

  typesensePBIs: ExistingPriceBookItem[];
  pbi: ExistingPriceBookItem | null;
  setPricebookItem: Dispatch<SetStateAction<ExistingPriceBookItem | null>>;
  onSelectItem: (tempEstimateItem: TemporaryEstimateItem) => void;
}): JSX.Element {
  const currency =
    args.siteKeyDoc?.customizations.accounting?.currency ?? "USD";

  if (args.pbi == null) {
    if (args.isSectionOpen === false) {
      return (
        <div className="rounded-md border border-red-600 bg-red-50 p-2 sm:max-w-fit sm:justify-self-center sm:pb-1">
          <BaseButtonSecondary
            type="button"
            onClick={() =>
              args.setDialogStates((prev) => ({ ...prev, pbi: true }))
            }
            className="w-full sm:w-64"
            textColor="text-black"
            borderColor="border-gray-700"
          >
            <AddCircleIcon fontSize="small" className="mr-2" />
            {strings.SELECT_PRICEBOOK_ITEM}
          </BaseButtonSecondary>

          <p className="pt-2 text-center text-xs font-medium tracking-wider text-red-800">
            REQUIRED
          </p>
        </div>
      );
    } else {
      return (
        <div className="-mt-1 w-full">
          {/* ^ to negate the margin-top in AddNewEstimateItemSelection */}
          <AddNewEstimateItemSelection
            onSearch={args.onFilterTextBoxChanged}
            PBItemQueryResultList={args.typesensePBIs}
            handleAddEstimateItem={args.onSelectItem}
            currency={currency}
            placeholder="Search for an item..."
          />
        </div>
      );
    }
  }
  return (
    <div className="relative mt-4 flex gap-2 rounded-2xl border border-gray-200 bg-white p-4 pt-5">
      <div className="absolute -top-[17.5px] left-[10.5px] h-[1.1rem] w-[8.2rem] rounded-t-lg bg-gray-200"></div>
      {/* ^ the empty div provides the border for "Price Book Item" */}
      <div className="absolute -top-4 left-3 rounded-t-md bg-white px-2 pt-0.5">
        Price Book Item
      </div>
      {/* TITLE AND DESCRIPTION */}
      <div className="flex w-full items-center">
        <div className="flex flex-col space-y-2">
          <span className="block whitespace-normal text-base leading-5">
            {args.pbi.title}
          </span>
          <span className="block whitespace-normal text-sm leading-5 text-gray-400">
            {args.pbi.description}
          </span>
          {args.pbi.discountable === false && (
            <p className={`text-sm italic`}>* Non-discountable</p>
          )}
        </div>
      </div>

      <div className="flex w-fit flex-col items-center justify-center gap-x-2 xs:flex-row sm:flex-col lg:flex-row">
        <span className="text-lg font-medium sm:text-base md:text-lg">
          {currencyFormatter(args.pbi.unitPrice, currency)}
        </span>
        <button
          className="rounded-full hover:bg-red-50"
          onClick={() => args.setPricebookItem(null)}
        >
          <TrashIcon
            aria-label="delete button"
            className="block h-10 w-10 cursor-pointer p-2 text-red-700"
          />
        </button>
      </div>
    </div>
  );
}

function getRenewalPBIComponent(args: {
  siteKeyDoc: ExistingSiteKey | null;
  onFilterTextBoxChanged: (searchTerm: string) => Promise<void>;

  renewalPBI: ExistingPriceBookItem | null;
  isSectionOpen: boolean;
  setDialogStates: Dispatch<SetStateAction<DialogStates>>;

  typesensePBIs: ExistingPriceBookItem[];
  onSelectItem: (tempItem: TemporaryEstimateItem) => void;
  setRenewalPBI: Dispatch<SetStateAction<ExistingPriceBookItem | null>>;

  isRenewalTemplateSelected: boolean;
}): JSX.Element {
  const currency =
    args.siteKeyDoc?.customizations.accounting?.currency ?? "USD";

  if (args.renewalPBI == null) {
    if (args.isSectionOpen === false) {
      return (
        <StyledTooltip
          title={
            args.isRenewalTemplateSelected
              ? `You cannot select a renewal price book item if a renewal template is selected.`
              : ""
          }
        >
          <div className="sm:justify-self-center">
            <BaseButtonSecondary
              type="button"
              disabled={args.isRenewalTemplateSelected}
              onClick={() =>
                args.setDialogStates((prev) => ({ ...prev, renewalPBI: true }))
              }
              className="w-full sm:mx-auto sm:w-64"
              textColor="text-black"
              borderColor="border-gray-700"
            >
              <AddCircleIcon fontSize="small" className="mr-2" />
              {strings.SELECT_RENEWAL_PRICEBOOK_ITEM}
            </BaseButtonSecondary>
          </div>
        </StyledTooltip>
      );
    } else {
      return (
        <div className="-mt-1 w-full">
          {/* ^ to negate the margin-top in AddNewEstimateItemSelection */}
          <AddNewEstimateItemSelection
            inputID="search-renewal-pbi"
            placeholder="Search for an item..."
            onSearch={args.onFilterTextBoxChanged}
            PBItemQueryResultList={args.typesensePBIs}
            handleAddEstimateItem={args.onSelectItem}
            currency={currency}
          />
        </div>
      );
    }
  }
  return (
    <div className="relative mt-4 flex gap-2 rounded-2xl border border-gray-200 bg-white p-4 pt-5">
      <div className="absolute -top-[17.5px] left-[10.5px] h-[1.1rem] w-[12.2rem] rounded-t-lg bg-gray-200"></div>
      {/* ^ the empty div provides the border for "Renewal Price Book Item" */}
      <div className="absolute -top-4 left-3 rounded-t-md bg-white px-2 pt-0.5">
        Renewal Price Book Item
      </div>
      {/* TITLE AND DESCRIPTION */}
      <div className="flex w-full items-center">
        <div className="flex flex-col space-y-2">
          <span className="block whitespace-normal text-base leading-5">
            {args.renewalPBI.title}
          </span>
          <span className="block whitespace-normal text-sm leading-5 text-gray-400">
            {args.renewalPBI.description}
          </span>
          {args.renewalPBI.discountable === false && (
            <p className={`text-sm italic`}>* Non-discountable</p>
          )}
        </div>
      </div>

      <div className="flex w-fit flex-col items-center justify-center gap-x-2 xs:flex-row sm:flex-col lg:flex-row">
        <span className="text-lg font-medium sm:text-base md:text-lg">
          {currencyFormatter(args.renewalPBI.unitPrice, currency)}
        </span>
        <button
          className="rounded-full hover:bg-red-50"
          onClick={() => args.setRenewalPBI(null)}
        >
          <TrashIcon
            aria-label="delete button"
            className="block h-10 w-10 cursor-pointer p-2 text-red-700"
          />
        </button>
      </div>
    </div>
  );
}

function getRenewalTemplateComponent(args: {
  /** templates to choose from */
  templateList: ExistingMembershipTemplate[];
  siteCurrency: string;
  renewalTemplate: ExistingMembershipTemplate | null;
  isSectionOpen: boolean;
  setDialogStates: Dispatch<SetStateAction<DialogStates>>;
  setRenewalTemplate: Dispatch<
    SetStateAction<ExistingMembershipTemplate | null>
  >;
  isRenewalPBISelected: boolean;
  onFilterTemplates: (searchTerm: string) => void;
}): JSX.Element {
  if (args.renewalTemplate == null) {
    if (args.isSectionOpen === false) {
      return (
        <StyledTooltip
          title={
            args.isRenewalPBISelected
              ? `You cannot select a renewal template if a renewal price book item is selected.`
              : ""
          }
        >
          <div className="sm:justify-self-center">
            <BaseButtonSecondary
              type="button"
              disabled={args.isRenewalPBISelected}
              onClick={() =>
                args.setDialogStates((prev) => ({
                  ...prev,
                  renewalTemplate: true,
                }))
              }
              className="w-full sm:mx-auto sm:w-64"
              textColor="text-black"
              borderColor="border-gray-700"
            >
              <AddCircleIcon fontSize="small" className="mr-2" />
              {strings.SELECT_RENEWAL_TEMPLATE}
            </BaseButtonSecondary>
          </div>
        </StyledTooltip>
      );
    } else {
      return (
        <Combobox
          value={args.renewalTemplate}
          onChange={args.setRenewalTemplate}
        >
          <div className="relative w-full">
            <div className="relative flex w-full cursor-default rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-300 sm:text-sm">
              <Combobox.Button className="w-full" as="div">
                <Combobox.Input
                  className={`block w-full min-w-0 flex-1 rounded border border-black p-4 text-gray-700 focus:border-primaryLight focus:outline-none  focus:ring focus:ring-primaryLight sm:text-sm`}
                  id="search-templates"
                  placeholder="Search for a template..."
                  onChange={(ev) => args.onFilterTemplates(ev.target.value)}
                  autoComplete="off"
                  // autoFocus
                />
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => {
                args.onFilterTemplates("");
                args.setDialogStates((prev) => ({
                  ...prev,
                  renewalTemplate: false,
                }));
              }}
            >
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                {args.templateList.length === 0 ? (
                  <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                    Nothing found.
                  </div>
                ) : (
                  args.templateList.map((template) => (
                    <Combobox.Option
                      key={template.id}
                      className={({ active }) =>
                        `relative cursor-default select-none px-4 py-2 ${
                          active ? "bg-gray-600 text-white" : "text-gray-900"
                        }`
                      }
                      value={template}
                    >
                      <div className="flex items-center justify-between gap-x-2">
                        <div className="flex flex-col">
                          <span className="block whitespace-normal text-base leading-5">
                            {template.title}
                          </span>
                          <span className="block whitespace-normal text-sm leading-5 text-gray-400">
                            {template.description}
                          </span>
                        </div>
                        <div>
                          {currencyFormatter(template.price, args.siteCurrency)}
                          /{template.invoiceFrequency}
                        </div>
                      </div>
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      );
    }
  } else {
    return (
      <div className="relative mt-4 max-w-fit rounded-2xl border border-gray-200 bg-white px-4 pb-1 pt-5">
        <div className="flex gap-2">
          <div className="absolute -top-[17.5px] left-[10.5px] h-[1.1rem] w-[9.35rem] rounded-t-lg bg-gray-200"></div>
          {/* ^ the empty div provides the border for "Renewal Template" */}
          <div className="absolute -top-4 left-3 rounded-t-md bg-white px-2 pt-0.5">
            {strings.RENEWAL_TEMPLATE}
          </div>
          <ul className="flex w-full flex-col items-start gap-x-3">
            <li className="text-base font-medium leading-5">
              {args.renewalTemplate.title}
            </li>
            <li className="text-sm leading-5 text-gray-400">
              {args.renewalTemplate.description}
            </li>
          </ul>

          <div className="flex w-fit flex-col items-center justify-center gap-x-2 xs:flex-row sm:flex-col lg:flex-row">
            <span className="text-lg font-medium sm:text-base md:text-lg">
              {currencyFormatter(args.renewalTemplate.price, args.siteCurrency)}
            </span>
            <button
              className="rounded-full hover:bg-red-50"
              onClick={() => args.setRenewalTemplate(null)}
            >
              <TrashIcon
                aria-label="delete button"
                className="block h-10 w-10 cursor-pointer p-2 text-red-700"
              />
            </button>
          </div>
        </div>
        <ul className="mt-2 flex flex-col items-start justify-around gap-x-3 gap-y-1 border-t border-gray-100 pt-1 text-xs font-medium uppercase text-gray-400 xs:flex-row xs:flex-wrap">
          <li>
            invoice method:{" "}
            {getReadableInvoiceMethod(args.renewalTemplate.invoiceMethod)}
          </li>
          <li>invoice frequency: {args.renewalTemplate.invoiceFrequency}</li>
          <li>
            default duration:{" "}
            {getReadableDefaultDuration(args.renewalTemplate.defaultDuration)}
          </li>
        </ul>
      </div>
    );
  }
}

function getTaskGenComponent(args: {
  setDialogStates: Dispatch<SetStateAction<DialogStates>>;
  taskGenerationList: TemporaryTaskGeneration[];
  deleteTask: (id: string) => void;
  editTask: (id: string) => void;
  control: Control<TemplateFormState>;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <span className="flex flex-col gap-4 sm:grid sm:grid-cols-2 sm:items-center sm:justify-items-center md:col-span-2 lg:col-span-3">
        <BaseButtonSecondary
          type="button"
          onClick={() =>
            args.setDialogStates((prev) => ({ ...prev, taskGen: true }))
          }
          className="w-full sm:w-52"
          textColor="text-black"
          borderColor="border-gray-700"
        >
          <MdFormatListBulleted className="mr-2 h-5 w-5" />
          {strings.ADD_TASK_TEMPLATE}
        </BaseButtonSecondary>

        <div className="max-w-fit rounded-md border border-gray-300 bg-white px-3 py-2 transition-colors hover:border-gray-500">
          <Controller
            name="automaticallyGenerateTasks"
            control={args.control}
            render={({ field }) =>
              args.taskGenerationList.length > 0 ? (
                <StyledSwitchGroup
                  readableName="Automatically Generate Tasks"
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  checked={field.value ?? false}
                  id="automaticallyGenerateTasks"
                  name={field.name}
                />
              ) : (
                <StyledTooltip title="Cannot enable this feature without adding at least one task.">
                  {/* yup... super hacky. can't have a ref in this function, but the tooltip needs a ref */}
                  <div ref={field.ref}>
                    <StyledSwitchGroup
                      disabled={true}
                      checked={false}
                      readableName="Automatically Generate Tasks"
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      ref={field.ref}
                      name={field.name}
                      id="disabled-automaticallyGenerateTasks"
                    />
                  </div>
                </StyledTooltip>
              )
            }
          />
        </div>
      </span>

      {/* want a little extra space if there are tasks */}
      {args.taskGenerationList.length > 0 && (
        <div className="mt-1 md:col-span-2 lg:col-span-3"></div>
      )}

      {args.taskGenerationList.map((task) => {
        return (
          <div
            key={task.id}
            className="relative grid grid-cols-[5fr,2fr,1fr] rounded-2xl border border-gray-200 bg-white p-4 pt-5 xxs:grid-cols-[3fr,4fr,1fr] md:grid-cols-[5fr,2fr,1fr]"
          >
            <div className="flex w-full items-center">
              <div className="flex flex-col space-y-2 text-base">
                <span className="block whitespace-normal leading-5">
                  <span className="font-medium text-gray-400">Job: </span>
                  {getReadableCraftType(task.craftType)}
                </span>
                <span className="block whitespace-normal leading-5">
                  <span className="font-medium text-gray-400">Type: </span>
                  {getReadableTaskType(task.taskType)}
                </span>
                <span className="block whitespace-normal leading-5">
                  <span className="font-medium text-gray-400">Status: </span>
                  {getReadableTaskStatus(task.taskStatus)}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-self-end">
              <span className="block whitespace-normal text-sm leading-5 text-gray-400">
                {task.description}
              </span>
            </div>

            {/* ACTION BUTTONS */}
            <div className="ml-2 flex flex-col">
              <button
                type="button"
                className="h-fit w-fit place-self-center justify-self-end rounded-full hover:bg-primaryOpacity90"
                onClick={() => args.editTask(task.id)}
              >
                <PencilIcon
                  aria-label="edit button"
                  className="block h-10 w-10 cursor-pointer p-2 text-gray-700"
                />
              </button>
              <button
                type="button"
                className="h-fit w-fit place-self-center justify-self-end rounded-full hover:bg-red-50"
                onClick={() => args.deleteTask(task.id)}
              >
                <TrashIcon
                  aria-label="delete button"
                  className="block h-10 w-10 cursor-pointer p-2 text-red-700"
                />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
